export type LikeVisibility = 'public' | 'anonymous';

export interface User {
  _id: string;
  googleId?: string;
  email: string;
  name: string;
  picture?: string;
  isOwner: boolean;
  betaApproved: boolean;
  likeVisibility: LikeVisibility | null;
  phone?: string;
  phoneVerifiedAt?: string;
  // Face recognition
  facePhotoKey?: string | null;
  hasFacePhoto?: boolean;
  createdAt: string;
  waitlistStatus?: WaitlistStatus | null;
}

export type WaitlistStatus = 'pending' | 'approved' | 'rejected';

export interface WaitlistAnswers {
  role: string;
  eventsPerMonth: string;
  teamSize: string;
  howHeard: string;
  phone: string;
}

export interface WaitlistEntry {
  _id: string;
  userId: string;
  answers: WaitlistAnswers;
  status: WaitlistStatus;
  submittedAt: string;
  reviewedAt?: string;
}

export type EventStatus = 'active' | 'ended';

export interface EventManagerPermissions {
  allowEditingEventDetails: boolean;
  allowEndingEvent: boolean;
  allowDeletingEvent: boolean;
  allowCreatingNewAlbum: boolean;
  allowDeletingOfPhotos: boolean;
  allowAddingNewPresets: boolean;
  allowAddingNewFtpAccount: boolean;
  allowManagingTeam: boolean;
}

export type EventManagerStatus = 'pending' | 'invited' | 'active';

// ── Notifications ─────────────────────────────────────────────────────────────

export type NotificationType = 'manager_invite';

export type NotificationActionTaken = 'accept' | 'decline';

export interface ManagerInviteData {
  eventId: string;
  eventName: string;
  inviterName: string;
  inviterEmail: string;
  inviterPicture?: string;
  managerId: string;
  permissions: EventManagerPermissions;
}

export interface AppNotification {
  _id: string;
  userId: string;
  type: NotificationType;
  data: ManagerInviteData;
  actionRequired: boolean;
  actionTaken: NotificationActionTaken | null;
  actionedAt: string | null;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type EventUserRole = 'creator' | 'manager';

export interface EventManager {
  _id: string;
  eventId: string;
  email: string;
  userId: Pick<User, '_id' | 'name' | 'email' | 'picture'> | null;
  addedBy: Pick<User, '_id' | 'name' | 'email'> | string;
  permissions: EventManagerPermissions;
  status: EventManagerStatus;
  invitedAt: string;
  claimedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Event {
  _id: string;
  name: string;
  description: string;
  status: EventStatus;
  createdBy: User | string;
  createdAt: string;
  endedAt?: string;
  photoCount?: number;
  ftpAccountCount?: number;
  albumCount?: number;
  userRole?: EventUserRole;
  userPermissions?: EventManagerPermissions | null;
  recentPhotoUrl?: string | null;
}

export interface FTPAccount {
  _id: string;
  eventId: string;
  username: string;
  passwordPlain: string;
  photographerName: string;
  createdBy: string;
  createdAt: string;
}

export interface Photo {
  _id: string;
  eventId: string;
  ftpAccountId: string | null;
  originalFilename: string;
  storagePath: string;
  thumbnailPath: string;
  fileSizeBytes: number;
  uploadedAt: string;
  thumbnailUrl: string | null;
  originalUrl: string | null;
  processedUrl?: string | null;
  processedThumbnailUrl?: string | null;
  uploadSource?: 'ftp' | 'manual';
  uploadedBy?: string;
  status?: 'processing' | 'ready' | 'failed';
  isHighlight?: boolean;
}

export interface UploadResult {
  filename: string;
  status: 'success' | 'failed';
  error?: string;
  photo?: Photo;
}

export interface Overlay {
  _id: string;
  eventId: string;
  name: string;
  storagePath: string;
  uploadedBy: User | string;
  createdAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface PhotosPage {
  photos: Photo[];
  nextCursor: string | null;
  hasMore: boolean;
  totalCount?: number;
  processingCount?: number;
}

export interface ExportTarget {
  label: string;
  platform: string;
  width: number;
  height: number;
}

// --- Editor stepper types ---

export type EditorStep = 'preset' | 'adjust' | 'crop' | 'overlay' | 'export';

export type OverlayAnchor =
  | 'top-left' | 'top-center' | 'top-right'
  | 'middle-left' | 'middle-center' | 'middle-right'
  | 'bottom-left' | 'bottom-center' | 'bottom-right';

export type OverlaySize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const OVERLAY_SIZE_LABELS: Record<OverlaySize, string> = {
  xs: 'Extra Small',
  sm: 'Small',
  md: 'Medium',
  lg: 'Large',
  xl: 'Extra Large',
};

/** Fraction of the shorter canvas dimension */
export const OVERLAY_SIZE_FACTORS: Record<OverlaySize, number> = {
  xs: 0.08,
  sm: 0.12,
  md: 0.18,
  lg: 0.26,
  xl: 0.36,
};

export interface OverlayConfig {
  overlayId: string;
  anchor: OverlayAnchor;
  size: OverlaySize;
  opacity: number; // 0-1
}

export interface ExportConfig {
  _id?: string;
  eventId: string;
  name: string;
  filters: {
    brightness: number;
    contrast: number;
    saturation: number;
    grayscale: boolean;
  };
  overlay: OverlayConfig | null;
  selectedPlatforms: string[];
  selectedFormats: string[]; // e.g. "Instagram-Square"
  createdAt?: string;
}

// ── Albums ──────────────────────────────────────────────────────────────────

/** Same shape as OverlayConfig — reused for album-level overlay stacking */
export type AlbumOverlayConfig = OverlayConfig;

export interface EventSummary {
  _id: string;
  name: string;
  description: string;
  status?: 'active' | 'ended';
}

export type ProcessingStatus = 'idle' | 'processing' | 'ready' | 'failed';

export type SlideshowTransition = 'crossfade' | 'slide' | 'zoom' | 'fade-to-black' | 'kenburns';
export type SlideshowPhotoOrder = 'newest' | 'oldest' | 'most_liked' | 'pinned_first' | 'random';
export type SlideshowMode = 'single' | 'collage' | 'mixed';
export type SlideshowCollageLayout = '2-up' | '3-up' | '4-grid' | 'mosaic';
export type SlideshowPhotoFit = 'fit' | 'fill' | 'stretch';

export interface SlideshowConfig {
  intervalSeconds: number;
  transition: SlideshowTransition;
  showPhotoCount: boolean;
  showAlbumName: boolean;
  showEventName: boolean;
  photoOrder: SlideshowPhotoOrder;
  /** 'single' = one photo per slide, 'collage' = every slide is a collage,
   *  'mixed' = a collage slide every `collageEveryN` single slides. */
  mode: SlideshowMode;
  collageLayout: SlideshowCollageLayout;
  /** Only meaningful when mode === 'mixed'. */
  collageEveryN: number;
  /** 'fit' letterboxes, 'fill' crops to cover, 'stretch' distorts to fill. */
  photoFit: SlideshowPhotoFit;
  /** When true, portrait photos are always letterboxed regardless of photoFit. */
  preservePortrait: boolean;
}

/**
 * The configuration we steer new users toward — offered as a one-click preset
 * so a non-technical venue owner never has to reason about intervals, collage
 * frequency or fitting algorithms to get a good-looking TV display.
 */
export const RECOMMENDED_SLIDESHOW_CONFIG: SlideshowConfig = {
  intervalSeconds: 8,
  transition: 'kenburns',
  showPhotoCount: true,
  showAlbumName: true,
  showEventName: true,
  photoOrder: 'random',
  mode: 'single',
  collageLayout: '4-grid',
  collageEveryN: 5,
  photoFit: 'fill',
  preservePortrait: true,
};

/** Defaults applied when an album predates the mode/collage fields. */
export const DEFAULT_SLIDESHOW_CONFIG: SlideshowConfig = {
  intervalSeconds: 5,
  transition: 'crossfade',
  showPhotoCount: true,
  showAlbumName: true,
  showEventName: true,
  photoOrder: 'newest',
  mode: 'single',
  collageLayout: '4-grid',
  collageEveryN: 5,
  photoFit: 'fit',
  preservePortrait: true,
};

export interface Album {
  _id: string;
  eventId: string;
  createdBy: User | string;
  name: string;
  description: string;
  inviteToken: string;
  tokenVersion: number;
  overlayConfigs: AlbumOverlayConfig[];
  coverPhotoId?: string | null;
  processingStatus?: ProcessingStatus;
  processedPhotoCount?: number;
  configVersion?: number;
  memberCount?: number;
  photoCount?: number;
  // Live album fields
  isLive?: boolean;
  sourceFilter?: 'all' | 'selected';
  sourceFtpAccountIds?: string[];
  sourceIncludeManualUploads?: boolean;
  // Face recognition fields
  faceRecognitionEnabled?: boolean;
  guestVisibility?: 'all' | 'matched_only';
  faceSearchScope?: 'album' | 'event';
  // Team access
  editAccess?: 'team' | 'creator_only';
  // Access restriction — email domains allowed to join. Empty/undefined = open.
  allowedEmailDomains?: string[];
  // Duplicate handling
  duplicateReviewEnabled?: boolean;
  // ZIP download fields
  zipStatus?: 'none' | 'building' | 'ready' | 'stale';
  zipPhotoCount?: number;
  zipBuiltAt?: string;
  highlightsZipStatus?: 'none' | 'building' | 'ready' | 'stale';
  // Slideshow fields
  slideshowEnabled?: boolean;
  slideshowToken?: string | null;
  /** Short, human-typeable display code (e.g. "K7RM-2PXQ"). */
  slideshowCode?: string | null;
  slideshowConfig?: SlideshowConfig;
  createdAt: string;
}

export type AlbumPhotoProcessedStatus = 'pending' | 'processed' | 'failed';

export interface AlbumPhoto {
  _id: string;
  albumId: string;
  photoId: string;
  addedAt: string;
  processedStatus: AlbumPhotoProcessedStatus;
  processedAt: string | null;
  likeCount: number;
  isPinned: boolean;
  pinnedAt: string | null;
  isLikedByMe?: boolean;
  /** Set when the ZIP builder couldn't read this photo's file (corrupt). */
  processedCorrupt?: boolean;
}

export interface AlbumMember {
  _id: string;
  albumId: string;
  userId: User;
  joinedAt: string;
  isKicked: boolean;
  notificationsEnabled?: boolean;
}

export interface AlbumPreview {
  album: {
    _id: string;
    name: string;
    overlayConfigs: AlbumOverlayConfig[];
    memberCount: number;
    photoCount: number;
    coverThumbnailUrl: string | null;
    allowedEmailDomains?: string[];
  };
  previewThumbnails: string[];
}

/** Album with `eventId` populated — returned by the view endpoint */
export type AlbumWithEvent = Omit<Album, 'eventId'> & { eventId: EventSummary | string };

export interface ActivityStats {
  totalPhotos: number;
  totalMembers: number;
  totalLikes: number;
  pendingProcessing?: number;
}

export interface AlbumViewData {
  album: AlbumWithEvent;
  photos: Photo[];
  nextCursor: string | null;
  hasMore: boolean;
  totalPhotoCount: number;
  photoMeta?: Record<string, { likeCount: number; isLikedByMe: boolean; isPinned: boolean }>;
  isCreator?: boolean;
  canEdit?: boolean;
  stats?: ActivityStats;
  memberNotificationsEnabled?: boolean;
  coverPhotoUrl?: string | null;
}

export interface MyAlbumEntry {
  _id: string;
  name: string;
  eventName: string;
  creatorName: string;
  photoCount: number;
  firstThumbnail: string | null;
  joinedAt: string;
}

export interface AdminDevToolsLog {
  _id: string;
  albumId: { _id: string; name: string };
  userId: { _id: string; name: string; email: string };
  detectedAt: string;
  method: 'resize' | 'keyboard';
}

// ─── Photo Reports ─────────────────────────────────────────────────────────

export type ReportReason = 'inappropriate' | 'privacy' | 'duplicate' | 'other';
export type ReportStatus = 'pending' | 'dismissed' | 'actioned';

export interface PhotoReport {
  _id: string;
  albumId: string;
  photoId: string;
  reportedBy: { _id: string; name: string; email: string; picture?: string } | string;
  reason: ReportReason;
  message: string;
  status: ReportStatus;
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: { _id: string; name: string } | null;
  thumbnailUrl: string | null;
  originalFilename: string | null;
}

// ─── Quota ──────────────────────────────────────────────────────────────────

export type QuotaType = 'events' | 'photosPerEvent' | 'albumsPerEvent' | 'ftpAccountsPerEvent' | 'liveAlbumsPerEvent';
export type QuotaRequestStatus = 'pending' | 'approved' | 'rejected';

export interface QuotaLimits {
  maxEvents: number;
  maxPhotosPerEvent: number;
  maxAlbumsPerEvent: number;
  maxFtpAccountsPerEvent: number;
  maxLiveAlbumsPerEvent: number;
}

export interface QuotaUsageEntry {
  used: number;
  limit: number;
}

export interface QuotaEventDetail {
  eventId: string;
  eventName: string;
  photos: QuotaUsageEntry;
  albums: QuotaUsageEntry;
  ftpAccounts: QuotaUsageEntry;
}

export interface QuotaUsage {
  limits: QuotaLimits;
  events: QuotaUsageEntry;
  eventDetails: QuotaEventDetail[];
}

export interface QuotaRequest {
  _id: string;
  userId: string | { _id: string; name: string; email: string; picture?: string };
  quotaType: QuotaType;
  currentLimit: number;
  requestedLimit?: number;
  reason?: string;
  status: QuotaRequestStatus;
  adminNote?: string;
  createdAt: string;
  reviewedAt?: string;
}
